import React, { useState } from 'react';
import { Save, Download, Upload, RefreshCw, Info } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Input, Select, FormRow, FormSection } from '../components/FormComponents';
import Breadcrumb from '../components/Breadcrumb';
import { exportToJSON } from '../utils/helpers';

export default function Settings() {
  const { state, dispatch, showNotification } = useApp();
  const [form, setForm] = useState({ ...state.settings });
  const [showReset, setShowReset] = useState(false);

  const handleSave = () => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: form });
    showNotification('Pengaturan berhasil disimpan');
  };

  const handleExportAll = () => {
    exportToJSON({
      prospects: state.prospects,
      spk: state.spk,
      delivery: state.delivery,
      team: state.team,
      events: state.events,
      settings: state.settings,
      exportedAt: new Date().toISOString(),
    }, 'honda-sales-backup');
    showNotification('Data berhasil di-export');
  };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Pengaturan' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pengaturan</h1>
          <p className="text-sm text-gray-500">Konfigurasi sistem manajemen penjualan Honda</p>
        </div>
        <button onClick={handleSave} className="btn-primary"><Save size={16}/>Simpan Pengaturan</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dealer Info */}
          <div className="card">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Informasi Dealer</h3>
            <div className="space-y-4">
              <Input label="Nama Dealer" value={form.cabang || ''} onChange={e => setForm({...form, cabang: e.target.value})} placeholder="Honda Authorized Dealer" />
              <FormRow>
                <Input label="Kota" value={form.kota || ''} onChange={e => setForm({...form, kota: e.target.value})} placeholder="Jakarta" />
                <Input label="Kode Dealer" value={form.kodeDealer || ''} onChange={e => setForm({...form, kodeDealer: e.target.value})} placeholder="HD-001" />
              </FormRow>
              <FormRow>
                <Input label="No. Telepon" value={form.telepon || ''} onChange={e => setForm({...form, telepon: e.target.value})} placeholder="021-XXXXXXXX" />
                <Input label="Email" type="email" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} placeholder="dealer@honda.co.id" />
              </FormRow>
            </div>
          </div>

          {/* Sales Targets */}
          <div className="card">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Target Penjualan</h3>
            <div className="space-y-4">
              <FormRow>
                <Input label="Target Bulanan (unit)" type="number" value={form.targetBulanan || 50} onChange={e => setForm({...form, targetBulanan: parseInt(e.target.value)})} min="1" />
                <Input label="Target Tahunan (unit)" type="number" value={form.targetTahunan || 600} onChange={e => setForm({...form, targetTahunan: parseInt(e.target.value)})} min="1" />
              </FormRow>
              <FormRow>
                <Input label="Target Revenue Bulanan (Rp)" type="number" value={form.targetRevenue || 5000000000} onChange={e => setForm({...form, targetRevenue: parseInt(e.target.value)})} />
                <Select label="Mata Uang" value={form.currency || 'IDR'} onChange={e => setForm({...form, currency: e.target.value})} options={[{value:'IDR',label:'IDR (Rupiah)'},{value:'USD',label:'USD'}]} />
              </FormRow>
            </div>
          </div>

          {/* Notification */}
          <div className="card">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Preferensi Notifikasi</h3>
            <div className="space-y-3">
              {[
                { key: 'notifNewLead', label: 'Notifikasi lead baru' },
                { key: 'notifDelivery', label: 'Notifikasi delivery' },
                { key: 'notifTarget', label: 'Notifikasi pencapaian target' },
                { key: 'notifFollowup', label: 'Pengingat follow-up otomatis' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <span className="text-sm text-gray-700">{label}</span>
                  <input
                    type="checkbox"
                    checked={form[key] !== false}
                    onChange={e => setForm({...form, [key]: e.target.checked})}
                    className="w-4 h-4 accent-red-600"
                  />
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar Settings */}
        <div className="space-y-4">
          {/* Data Management */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Manajemen Data</h3>
            <div className="space-y-3">
              <button onClick={handleExportAll} className="btn-secondary w-full justify-center">
                <Download size={16}/>Backup Semua Data
              </button>
              <label className="btn-secondary w-full justify-center cursor-pointer">
                <Upload size={16}/>Import Data
                <input type="file" accept=".json" className="hidden" onChange={() => showNotification('Fitur import sedang dalam pengembangan', 'info')} />
              </label>
            </div>
          </div>

          {/* System Info */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Info size={15} className="text-blue-500" /> Informasi Sistem
            </h3>
            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex justify-between"><span>Versi</span><span className="font-medium">1.0.0</span></div>
              <div className="flex justify-between"><span>Total Prospek</span><span className="font-medium">{state.prospects.length}</span></div>
              <div className="flex justify-between"><span>Total SPK</span><span className="font-medium">{state.spk.length}</span></div>
              <div className="flex justify-between"><span>Total Tim</span><span className="font-medium">{state.team.length}</span></div>
              <div className="flex justify-between"><span>Total Event</span><span className="font-medium">{state.events.length}</span></div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="card border border-red-100">
            <h3 className="text-sm font-semibold text-red-600 mb-3">Zona Berbahaya</h3>
            <p className="text-xs text-gray-500 mb-3">Tindakan di bawah ini tidak dapat dibatalkan</p>
            <button
              onClick={() => setShowReset(!showReset)}
              className="btn-danger w-full justify-center text-xs"
            >
              <RefreshCw size={14}/>Reset Data Demo
            </button>
            {showReset && (
              <div className="mt-3 p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 mb-2">Fitur ini akan mereset semua data ke kondisi awal. Pastikan sudah backup data Anda.</p>
                <button onClick={() => { window.location.reload(); }} className="w-full py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700">
                  Ya, Reset Sekarang
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
